import { Injectable } from '@angular/core';
import { EMPTY, Observable, of, throwError } from 'rxjs';

import { BetaFlagsRepository, EntitiesRepository, QuoteRepository, UsersRepository } from '@data-layer/repositories';
import {
  ChangePasswordModel,
  ConfirmEmailModel,
  RegisteredUserModel,
  RegisterUserModel,
  ResetPasswordModel,
  RespConfirmModel,
  TokenModel,
  UserBetaFlagModel,
  UserModel,
} from '@data-layer/models/user';
import { ILoginForm } from '@app/modules/auth/interfaces';
import { QuoteModel, QuoteUpdateModel } from '@data-layer/models/lcl';
import { CompanyModel } from '@data-layer/models/companies';
import { enumShippingRole, enumUserBetaFlag } from '@data-layer/enums/user';
import { enumIncotermsType } from '@data-layer/enums/lcl/enumIncotermsType';
import { enumExternalQuoteState } from '@data-layer/enums/lcl';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { enumGATagEventCategory } from '@enum/enumGATagEventCategory';
import { enumGATagEventAction } from '@enum/enumGATagEventAction';
import { enumGATagEventLabel } from '@enum/enumGATagEventLabel';
import { FetchLegalTermsAndPrivacyPolicyUpdatedDate } from '@app/shared/state';
import { enumRespErrors } from '@data-layer/enums/common';
import { TokenService } from '@services/token.service';
import { GoogleAnalyticsService } from '@services/google-analytics.service';
import { UntypedFormGroup } from '@angular/forms';
import { CompaniesService } from '@services/companies.service';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngxs/store';
import { EntityModel } from '@data-layer/models/entities';
import { enumEntityModelCode } from '@data-layer/enums/entities';
import { BetaFeatureSearchModel } from '@app/data-layer/models/common/beat-feature-search-model';
import { ACCESS_TOKEN } from '@constants/setting-keys.const';
import { JwtHelperService } from '@auth0/angular-jwt';

@Injectable()
export class AuthService {
  private loginData: ILoginForm;
  private otherShippingRoles: enumShippingRole[] = [enumShippingRole.OTHER, enumShippingRole.AGENT];

  constructor(
    private jwtHelperService: JwtHelperService,
    private betaFlagsRepository: BetaFlagsRepository,
    private usersRepository: UsersRepository,
    private quoteRepository: QuoteRepository,
    private tokenService: TokenService,
    private ga: GoogleAnalyticsService,
    private companiesService: CompaniesService,
    private route: ActivatedRoute,
    private store: Store,
    private entitiesRepository: EntitiesRepository,
  ) {}

  public signUp(obj: RegisterUserModel, recaptchaToken: string): Observable<RegisteredUserModel> {
    return this.usersRepository.create(obj, recaptchaToken);
  }

  public confirmEmail(obj: ConfirmEmailModel): Observable<RespConfirmModel> {
    return this.usersRepository.confirmEmail(obj);
  }

  public login(obj: Object): Observable<TokenModel> {
    return this.usersRepository.login(obj);
  }

  public acceptInvitedUserEmail(token: string): Observable<never> {
    return this.usersRepository.acceptInvitedUserEmail(token);
  }

  public resendActivationEmail(userId: string, email?: string) {
    const params: { userId?: string; email?: string } = {};
    if (userId) {
      Object.assign(params, { userId });
    }
    if (email) {
      Object.assign(params, { email });
    }
    return this.usersRepository.resendConfirmationEmail(params);
  }

  public forgotPassword(email: string) {
    return this.usersRepository.forgotPassword(email);
  }

  public restorePassword(data: ResetPasswordModel, recaptchaToken: string) {
    return this.usersRepository.resetPassword(data, recaptchaToken);
  }

  public changePassword(data: ChangePasswordModel): Observable<UserModel> {
    return this.usersRepository.changePassword(data);
  }

  public createQuote(quote: QuoteUpdateModel, company: CompanyModel, token: string, isSkipCreateEntity?: boolean): Observable<QuoteModel> {
    let shippingRole: enumShippingRole = company.shippingRole;
    let incoTermsType: enumIncotermsType = quote.incoTermsType;
    const user: UserModel = this.companiesService.getUser();

    if (this.otherShippingRoles.includes(company.shippingRole)) {
      shippingRole = [enumIncotermsType.FCA, enumIncotermsType.FOB].includes(quote.incoTermsType)
        ? enumShippingRole.IMPORTER
        : enumShippingRole.SHIPPER;
    }
    if (quote.shippingRole !== company.shippingRole && !this.otherShippingRoles.includes(company.shippingRole)) {
      incoTermsType = this.mapShippingRoleToIncoterms(quote.incoTermsType);
    }

    const data: QuoteUpdateModel = {
      ...quote,
      companyId: company?.id,
      externalQuoteState: enumExternalQuoteState.EXTERNAL_CREATED,
      incoTermsType,
      shippingRole,
    };

    const shipper: EntityModel = this.prepareConsigneeForLCL(company, user, data);

    return (isSkipCreateEntity ? of(null) : this.entitiesRepository.createEntity(company?.id, shipper)).pipe(
      switchMap((entity: EntityModel) =>
        this.quoteRepository.create(
          {
            ...data,
            ...(shipper.entityCode === enumEntityModelCode.CONSIGNEE && !isSkipCreateEntity && { consigneeId: entity?.id }),
            ...(shipper.entityCode === enumEntityModelCode.SHIPPER && !isSkipCreateEntity && { shipperId: entity?.id }),
          },
          token,
        ),
      ),
    );
  }

  public resetLoginData(): void {
    this.tokenService.eraseTokenState();
  }

  public loginAction(username: string, password: string, formGroup: UntypedFormGroup, isSkipErrors?: boolean): Observable<CompanyModel> {
    return this.login({ password, username }).pipe(
      catchError((e) => {
        if (e instanceof HttpErrorResponse && e.status === 400) {
          if (e.error && e.error.error_details) {
            formGroup.setErrors({
              resError: {
                message: this.getResponseErrorMessage(e.error.error_details.error),
                code: e.error.error_details.error,
                params:
                  e.error.error_details.error === enumRespErrors.AccountTemporarilyLocked
                    ? { minutes: e.error.error_details.lockout_time || 5 }
                    : {},
              },
            });
          }
        }

        if (isSkipErrors) {
          return throwError(e);
        }

        return EMPTY;
      }),
      tap(() => {
        this.ga.emitCustomEvent(enumGATagEventCategory.Auth, enumGATagEventAction.SignIn, enumGATagEventLabel.LoginSuccess);
      }),
      switchMap((res) => {
        if (!!res) {
          this.tokenService.setToken(res);
        }

        return this.companiesService.getUserData(false, true);
      }),
      tap((user: UserModel) => {
        if (this.tokenService.getRoles()) {
          this.companiesService.setUserRoles(this.tokenService.getRoles());
        }
        const uid = this.tokenService.getUID();
        if (uid && user && user.uid !== uid) {
          this.companiesService.setUserUID(uid);
        }
      }),
      tap(() => {
        this.ga.setUser();
      }),
      switchMap((user) => {
        const userExternalId = this.userExternalId;
        return this.getUserBetaFlags({
          userExternalId,
          companyId: user.defaultCompanyId,
          organizationId: user.defaultOrganizationId,
        });
      }),
      switchMap((betaFlags) => {
        this.companiesService.setUserBetaFlags(betaFlags);
        return this.companiesService.getCompanyAsync(false, true);
      }),
      switchMap((company: CompanyModel) => {
        const token: string = this.tokenService.getAccessToken();
        const lclData: QuoteUpdateModel = !!this.route.snapshot.queryParams?.lclData
          ? JSON.parse(this.route.snapshot.queryParams.lclData)
          : null;
        return !!lclData && !!token && !!company
          ? this.createQuote(lclData, company, this.tokenService.getAccessToken()).pipe(map(() => company))
          : of(company);
      }),
      tap(() => {
        this.store.dispatch(new FetchLegalTermsAndPrivacyPolicyUpdatedDate());
      }),
      take(1),
    );
  }

  public get getLoginData() {
    return this.loginData;
  }

  public set setLoginData(data: ILoginForm) {
    this.loginData = data;
  }

  private getUserBetaFlags(params: BetaFeatureSearchModel): Observable<UserBetaFlagModel[]> {
    return this.betaFlagsRepository.getAppBetaFeatures(params).pipe(
      map((res: UserBetaFlagModel[]) => {
        const isfValue: UserBetaFlagModel = {
          id: 0,
          displayName: enumUserBetaFlag.isfDesktop,
          tag: enumUserBetaFlag.isfDesktop,
        };
        const esBrokerValue: UserBetaFlagModel = {
          id: 0,
          displayName: enumUserBetaFlag.esBrokerDesktop,
          tag: enumUserBetaFlag.esBrokerDesktop,
        };

        // Enable ISF & ES-Broker desktop for all
        return [...res, isfValue, esBrokerValue];
      }),
    );
  }

  private mapShippingRoleToIncoterms(incoTermsType: enumIncotermsType): enumIncotermsType {
    switch (incoTermsType) {
      case enumIncotermsType.FCA:
        return enumIncotermsType.DAT;

      case enumIncotermsType.FOB:
        return enumIncotermsType.CPT_CFR;

      case enumIncotermsType.DAT:
        return enumIncotermsType.FCA;

      case enumIncotermsType.CPT_CFR:
        return enumIncotermsType.FOB;

      default:
        return incoTermsType;
    }
  }

  private getResponseErrorMessage(code: enumRespErrors): string {
    let message: string;
    switch (code) {
      case enumRespErrors.AccountTemporarilyLocked:
        message = 'FORM_ERROR.LOCKED_ACCOUNT';
        break;
      case enumRespErrors.InvalidUsernameOrPassword:
        message = 'FORM_ERROR.INVALID_CREDENTIALS';
        break;
      case enumRespErrors.EmailNotConfirmed:
        message = 'FORM_ERROR.EMAIL_IS_NOT_CONFIRM';
        break;
      case enumRespErrors.AccountBlocked:
        message = 'FORM_ERROR.ACCOUNT_BLOCKED';
        break;
    }
    return message;
  }

  private prepareConsigneeForLCL(company: CompanyModel, user: UserModel, quote: QuoteUpdateModel): EntityModel {
    const { address, dateOfBirth, identifier, identifierQualifier, phone } = company;
    const name: string = company?.businessName || `${company?.firstName || ''} ${company?.lastName || ''}`;

    const shipper: EntityModel = new EntityModel({
      address: { ...address, id: null },
      addressId: null,
      entityCode: quote?.shippingRole === enumShippingRole.IMPORTER ? enumEntityModelCode.CONSIGNEE : enumEntityModelCode.SHIPPER,
      dateOfBirth,
      name,
      entityIdentifier: identifier,
      entityIdentifierQualifier: identifierQualifier,
      email: user?.email,
      phone: phone,
    });

    return shipper;
  }

  private get userExternalId(): string {
    return this.jwtHelperService.decodeToken(localStorage.getItem(ACCESS_TOKEN))?.user_external_id || null;
  }
}
